import { Injectable, inject } from '@angular/core';

import { DatabaseService } from '../database.service';
import { HttpCacheEntry } from '../models/offline-http.model';
import { StorageService } from '../storage.service';

/**
 * The kept response of every GET a teacher's screens make.
 *
 * Writing is best-effort by design. A response that cannot be kept - the store
 * is nearly full, the body is huge, IndexedDB is unavailable - is simply not
 * kept: the screen already has its data from the network, and failing the
 * request over a cache write would turn a convenience into an outage.
 */
@Injectable({ providedIn: 'root' })
export class HttpCacheRepository {
  private readonly databaseService = inject(DatabaseService);
  private readonly storage = inject(StorageService);

  /** Past this, one response is not worth a slot in a quota-bound store. */
  static readonly MAX_BODY_CHARS = 5_000_000;
  /** How stale the quota reading may be before a write re-measures it. */
  private static readonly QUOTA_RECHECK_MS = 60_000;

  async get(key: string): Promise<HttpCacheEntry | undefined> {
    if (!this.usable()) return undefined;
    try {
      const db = await this.databaseService.ready();
      return await db.httpCache.get(key);
    } catch {
      return undefined;
    }
  }

  /** Keep a fresh response. Replaces any earlier entry, clearing its invalidation. */
  async put(entry: HttpCacheEntry): Promise<void> {
    if (!this.usable()) return;
    try {
      const size = JSON.stringify(entry.body)?.length ?? 0;
      if (size > HttpCacheRepository.MAX_BODY_CHARS) return;

      // Measured at most once a minute: estimate() on every GET would cost more
      // than the caching saves. Cache growth is optional, so it yields to the
      // teacher's own writes when the store is nearly full.
      const measuredAt = this.storage.estimate().measuredAt;
      if (Date.now() - measuredAt > HttpCacheRepository.QUOTA_RECHECK_MS) {
        await this.storage.refresh();
      }
      if (this.storage.isCritical()) return;

      const db = await this.databaseService.ready();
      await db.httpCache.put({ ...entry, invalidatedAt: null });
    } catch (error) {
      console.warn('[offline] could not keep a local copy of a response', error);
    }
  }

  /**
   * Mark this teacher's kept responses matching `affects` as out of date.
   * Returns how many were marked.
   */
  async invalidate(
    userKey: string,
    affects: (entry: HttpCacheEntry) => boolean,
  ): Promise<number> {
    if (!this.usable()) return 0;
    try {
      const db = await this.databaseService.ready();
      const now = Date.now();
      return await db.httpCache
        .where('userKey')
        .equals(userKey)
        .filter(affects)
        .modify({ invalidatedAt: now });
    } catch (error) {
      console.warn('[offline] could not mark kept responses out of date', error);
      return 0;
    }
  }

  /**
   * Rewrite kept bodies in place. `rewrite` returns the new body, or null to
   * leave an entry as it is. When and whether an entry was fetched is not
   * changed - it is the same response with local work written into it.
   */
  async rewrite(
    userKey: string,
    rewrite: (entry: HttpCacheEntry) => unknown | null,
  ): Promise<number> {
    if (!this.usable()) return 0;
    try {
      const db = await this.databaseService.ready();
      let changed = 0;
      await db.httpCache
        .where('userKey')
        .equals(userKey)
        .modify((entry) => {
          const body = rewrite(entry);
          if (body === null) return;
          entry.body = body;
          changed++;
        });
      return changed;
    } catch (error) {
      console.warn('[offline] could not update kept responses', error);
      return 0;
    }
  }

  async count(): Promise<number> {
    if (!this.usable()) return 0;
    try {
      const db = await this.databaseService.ready();
      return await db.httpCache.count();
    } catch {
      return 0;
    }
  }

  async clear(): Promise<void> {
    if (!this.usable()) return;
    const db = await this.databaseService.ready();
    await db.httpCache.clear();
  }

  private usable(): boolean {
    return this.databaseService.isAvailable && !this.databaseService.fatalError;
  }
}
